'use client';
import { useEffect, useRef, useCallback } from 'react';
import {
  getZones,
  findZoneForLocation,
  getActiveTimeLog,
  saveTimeLog,
  generateId,
  distanceMeters,
  TimeLog,
} from '@/lib/storage';

const DWELL_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const DWELL_RADIUS_M = 150; // consider "same area" if within 150m
const POLL_INTERVAL_MS = 60 * 1000; // check every 60s
const POLL_INTERVAL_LOW_BATTERY_MS = 180 * 1000; // 3 min when battery < 20%

interface DwellState {
  lat: number;
  lng: number;
  since: number; // timestamp
  notified: boolean;
}

interface GeofencingCallbacks {
  onTempZoneDetected: (lat: number, lng: number, durationMinutes: number) => void;
  onZoneEntered: (zoneName: string) => void;
  onZoneExited: (zoneName: string, durationMinutes: number) => void;
}

// ─── Battery-aware poll interval ─────────────────────────────────────────────
async function getPollInterval(): Promise<number> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav = navigator as any;
    if (nav?.getBattery) {
      const battery = await nav.getBattery();
      if (!battery.charging && battery.level < 0.2) {
        return POLL_INTERVAL_LOW_BATTERY_MS;
      }
    }
  } catch {
    // Battery API not available — use default
  }
  return POLL_INTERVAL_MS;
}

export function useGeofencing(callbacks: GeofencingCallbacks) {
  const dwellRef = useRef<DwellState | null>(null);
  const lastZoneIdRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callbacksRef = useRef(callbacks);
  // Deduplication: track last entry log id to avoid double-saving
  const lastEntryLogIdRef = useRef<string | null>(null);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const handlePosition = useCallback((pos: GeolocationPosition) => {
    const lat = pos.coords?.latitude;
    const lng = pos.coords?.longitude;

    // Guard against invalid coordinates
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;

    const now = Date.now();

    // --- Zone matching (wrapped for safety) ---
    let matchedZone: ReturnType<typeof findZoneForLocation> = undefined;
    try {
      matchedZone = findZoneForLocation(lat, lng);
    } catch (err) {
      console.error('[geofencing] findZoneForLocation error:', err);
    }
    const currentZoneId = matchedZone?.id ?? null;

    if (currentZoneId !== lastZoneIdRef.current) {
      // Exited previous zone
      if (lastZoneIdRef.current) {
        try {
          const activeLog = getActiveTimeLog();
          if (activeLog) {
            const endTime = new Date().toISOString();
            const startMs = new Date(activeLog.startTime).getTime();
            const durationMinutes = isNaN(startMs) ? 0 : Math.max(0, Math.round((now - startMs) / 60000));
            saveTimeLog({
              ...activeLog,
              status: 'completed',
              endTime,
              durationMinutes,
            });
            const zones = getZones();
            const exitedZone = zones.find((z) => z.id === lastZoneIdRef.current);
            if (exitedZone) {
              callbacksRef.current.onZoneExited(exitedZone.name, durationMinutes);
            }
          }
        } catch (err) {
          console.error('[geofencing] Failed to save zone exit log:', err);
        }
      }

      // Entered new zone — deduplicate by checking active log
      if (matchedZone) {
        try {
          // Avoid creating duplicate entry logs for the same zone
          const existingActive = getActiveTimeLog();
          const alreadyLogged = existingActive?.zoneId === matchedZone.id;
          if (!alreadyLogged) {
            const newLogId = generateId();
            const newLog: TimeLog = {
              id: newLogId,
              zoneId: matchedZone.id,
              zoneName: matchedZone.name,
              category: matchedZone.type === 'work' ? 'work' : matchedZone.type === 'other' ? 'other' : 'work',
              status: 'active',
              startTime: new Date().toISOString(),
              endTime: null,
              durationMinutes: 0,
              lat,
              lng,
              isTemp: false,
              createdAt: new Date().toISOString(),
            };
            saveTimeLog(newLog);
            lastEntryLogIdRef.current = newLogId;
            callbacksRef.current.onZoneEntered(matchedZone.name);
            dwellRef.current = null; // reset dwell tracking
          }
        } catch (err) {
          console.error('[geofencing] Failed to save zone entry log:', err);
        }
      }

      lastZoneIdRef.current = currentZoneId;
    }

    // --- Dwell detection (only when NOT in a known zone) ---
    if (!matchedZone) {
      const dwell = dwellRef.current;
      if (!dwell) {
        dwellRef.current = { lat, lng, since: now, notified: false };
      } else {
        const movedFar = distanceMeters(lat, lng, dwell.lat, dwell.lng) > DWELL_RADIUS_M;
        if (movedFar) {
          // Reset dwell anchor
          dwellRef.current = { lat, lng, since: now, notified: false };
        } else {
          const dwellMs = now - dwell.since;
          if (dwellMs >= DWELL_THRESHOLD_MS && !dwell.notified) {
            dwellRef.current = { ...dwell, notified: true };
            const durationMinutes = Math.max(0, Math.round(dwellMs / 60000));

            // Create a temp "to_be_confirmed" log if none active
            try {
              const activeLog = getActiveTimeLog();
              if (!activeLog) {
                const tempLog: TimeLog = {
                  id: generateId(),
                  zoneId: null,
                  zoneName: 'Unknown Location',
                  category: 'unconfirmed',
                  status: 'to_be_confirmed',
                  startTime: new Date(dwell.since).toISOString(),
                  endTime: new Date().toISOString(),
                  durationMinutes,
                  lat: dwell.lat,
                  lng: dwell.lng,
                  isTemp: true,
                  createdAt: new Date().toISOString(),
                };
                saveTimeLog(tempLog);
                callbacksRef.current.onTempZoneDetected(dwell.lat, dwell.lng, durationMinutes);
              }
            } catch (err) {
              console.error('[geofencing] Failed to save temp dwell log:', err);
            }
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    // Initial position
    navigator.geolocation.getCurrentPosition(handlePosition, () => {}, {
      enableHighAccuracy: true,
      timeout: 10000,
    });

    // Battery-aware polling
    let currentInterval = POLL_INTERVAL_MS;
    const startPolling = (interval: number) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      currentInterval = interval;
      intervalRef.current = setInterval(() => {
        navigator.geolocation.getCurrentPosition(handlePosition, () => {}, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
        // Re-check battery every 5 polls
      }, interval);
    };

    // Start with default, then adjust based on battery
    startPolling(POLL_INTERVAL_MS);
    getPollInterval().then((interval) => {
      if (interval !== currentInterval) startPolling(interval);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [handlePosition]);
}
